import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import ExpansionPanel from '@material-ui/core/ExpansionPanel';
import ExpansionPanelSummary from '@material-ui/core/ExpansionPanelSummary';
import ExpansionPanelDetails from '@material-ui/core/ExpansionPanelDetails';
import ExpansionPanelActions from '@material-ui/core/ExpansionPanelActions';
import Typography from '@material-ui/core/Typography';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import moment from 'moment-timezone';
import Grid from '@material-ui/core/Grid';
import { useSelector } from 'react-redux';
import GoogleMapReact from 'google-map-react';
import HomeIcon from '@material-ui/icons/HomeRounded';
import BusinessIcon from '@material-ui/icons/DomainRounded';
import UserIcon from 'mdi-material-ui/AccountCheck';
import Button from '@material-ui/core/Button';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListSubheader from '@material-ui/core/ListSubheader';
import ListItemText from '@material-ui/core/ListItemText';
import Checkbox from '@material-ui/core/Checkbox';
import ListItemIcon from '@material-ui/core/ListItemIcon';

import * as constants from '../../../constants';
import ActivityReport from '../../Dialogs/ActivityReport';
import EditPostOrder from '../../Dialogs/EditPostOrder';


const createMapOptions = (maps) => {
  return {
    panControl: false,
    mapTypeControl: false,
    scrollwheel: false,
    zoomControl: true,
    fullscreenControl: false
  }
};

const useStyles = makeStyles(theme => ({
  list: {
    width: '100%'
  },
  map: {
    height: 300,
    width: '100%',
    borderRadius: 8,
    overflow: 'hidden'
  },
  userMarker: {
    height: 32,
    width: 32,
    borderRadius: 16,
    backgroundColor: 'white',
    display: 'flex',
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
    top: '50%',
    left: '50%'
  },
  locationMarker: {
    height: 32,
    width: 32,
    borderRadius: 16,
    backgroundColor: 'white',
    display: 'flex',
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center'
  },
  locationMarkerIcon: {
    color: 'black',
    width: 24,
    height: 24
  },
  userMarkerIcon: {
    color: 'black',
    width: 24,
    height: 24
  },
  secondaryHeading: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.text.secondary,
  },
  secondary: {
    flex: 1,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end'
  },
  heading: {
    fontSize: theme.typography.pxToRem(15)
  },
  expansionSummary: {
    height: 56
  }
}));

const PostOrder = ({ postOrder, isEditable, timezone, index }) => {
  const [showActivityReport, setShowActivityReport] = useState(false);
  const [showEditPostOrder, setShowEditPostOrder] = useState(false);

  const classes = useStyles();
  const { time, propertyId, dar, completed, description } = postOrder;

  const property = useSelector(state => {
    const propertyId = postOrder.propertyId
    if (propertyId) return state.properties.byId[postOrder.propertyId]
    else return null
  })

  const getMapBounds = (map, maps, locations) => {
    const bounds = new maps.LatLngBounds();
  
    for(const location of locations){
      bounds.extend(
        new maps.LatLng(location.lat, location.lng),
      );
    };
    return bounds;
  };

  const apiIsLoaded = (map, maps, locations) => {
    if (map) {
      const bounds = getMapBounds(map, maps, locations);
      map.fitBounds(bounds);
    }
  };

  const renderIcon = (variant) => {
    switch(variant) {
      case 'RESIDENTIAL':
        return <HomeIcon className={classes.locationMarkerIcon} />
      case 'COMMERCIAL':
        return <BusinessIcon className={classes.locationMarkerIcon} />
      default:
        return <HomeIcon className={classes.locationMarkerIcon} />
    }
  }

  const renderContent = () => {
    if(time || propertyId) {
      return (
        <>
          {
            time && (
              <>
                <ListSubheader disableSticky>Time window</ListSubheader>
                <ListItem>
                  <ListItemText primary={'Start'} secondary={moment(time.start).tz(timezone).format(constants.moment.format)} />
                </ListItem>
                <ListItem>
                  <ListItemText primary={'End'} secondary={moment(time.end).tz(timezone).format(constants.moment.format)} />
                </ListItem>
              </>
            )
          } 
          {
            propertyId &&  (
              <>
                <ListSubheader disableSticky>Location</ListSubheader>
                <ListItem>
                  <ListItemText primary={property.displayName} secondary={property.place.formattedAddress} />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Checkbox
                      checked={dar.isRequired}
                      inputProps={{
                        readOnly: true
                      }}
                      edge='start'
                    />
                  </ListItemIcon>
                  <ListItemText secondary={'Daily activity report required'}/>
                </ListItem>
                <ListItem>
                  <div className={classes.map}>
                    <GoogleMapReact
                      bootstrapURLKeys={{
                        key: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
                      }}
                      options={createMapOptions}
                      defaultCenter={property.place.coordinates}
                      defaultZoom={16}
                      yesIWantToUseGoogleMapApiInternals
                      onGoogleApiLoaded={
                        ({ map, maps }) => {
                          completed &&
                          apiIsLoaded(
                            map, 
                            maps, 
                            [property.place.coordinates, completed.coordinates]
                          )
                        }
                      }
                    >
                      {
                        completed &&
                        <div 
                          className={classes.userMarker}
                          lat={completed.coordinates.lat}
                          lng={completed.coordinates.lng}
                        >
                          <UserIcon className={classes.userMarkerIcon} />
                        </div>
                      }
                      <div 
                        className={classes.locationMarker}
                        lat={property.place.coordinates.lat}
                        lng={property.place.coordinates.lng}
                      >
                        { renderIcon(property.variant) }
                      </div>
                    </GoogleMapReact>
                  </div>
                </ListItem>
              </>
            )
          }
        </>
      )
    } else {
      return (
        <Grid item xs={12} fullWidth>
          <Typography>No additional details</Typography>
        </Grid>
      )
    }
    
  }

  return (
    <ListItem>
      <ExpansionPanel className={classes.list}>
        <ExpansionPanelSummary
          className={classes.expansionSummary}
          expandIcon={<ExpandMoreIcon />}
        >
          <Grid container spacing={2} alignItems='center'>
            <Grid item xs={8}>
              <Typography className={classes.heading}>
                {description}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <div className={classes.secondary}>
                {
                  (
                    completed ? (
                      <Typography className={classes.secondaryHeading}>
                        {`Completed ${moment(completed.timestamp).format(constants.moment.format)}`}
                      </Typography>
                    ) : (
                      isEditable ? (
                        <Button 
                          color='primary'
                          onClick={
                            event => { 
                              event.stopPropagation()
                              setShowEditPostOrder(true)
                            }
                          }
                          onFocus={event => event.stopPropagation()}
                          size='small'
                        >
                          Edit
                        </Button>
                      ) : (
                          <Typography className={classes.secondaryHeading}>
                            {`Not completed`}
                          </Typography>
                        )
                      )
                    )
                }
              </div>
            </Grid>
          </Grid>
        </ExpansionPanelSummary>
        <ExpansionPanelDetails>
          <List className={classes.list}>
            {
              renderContent()
            }
          </List>
        </ExpansionPanelDetails>
        {
          dar.darId && (
            <ExpansionPanelActions>
              <Button 
                size='small' 
                color='primary' 
                variant='contained'
                onClick={() => setShowActivityReport(true)}
              >
                DAR
              </Button>
            </ExpansionPanelActions>
          )
        }
      </ExpansionPanel>
      {
        dar.darId && (
          <ActivityReport 
            isOpen={showActivityReport} 
            onClose={() => setShowActivityReport(false)} 
            reportId={dar.darId}
            onSuccess={() => setShowActivityReport(false)}
          />
        )
      }
      <EditPostOrder 
        isOpen={showEditPostOrder}
        onClose={() => setShowEditPostOrder(false)}
        index={index}
        timezone={timezone}
      />
    </ListItem>
  )
}

export default PostOrder;